import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkExportModal = ({ isOpen, onClose, verses = [] }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportFilters, setExportFilters] = useState({
    status: 'all',
    bucket: 'all',
    hasNotes: false,
    hasLinks: false
  });
  const [exportFields, setExportFields] = useState({
    reference: true,
    text: true,
    version: true,
    status: false,
    progress: false,
    tags: false,
    notes: false,
    dateAdded: false,
    lastStudied: false
  });
  const [isExporting, setIsExporting] = useState(false);

  const formatOptions = [
    { value: 'csv', label: 'CSV File (.csv)' },
    { value: 'txt', label: 'Text File (.txt)' },
    { value: 'json', label: 'JSON File (.json)' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'new', label: 'New' },
    { value: 'learning', label: 'Learning' },
    { value: 'mastered', label: 'Mastered' }
  ];

  const bucketOptions = [
    { value: 'all', label: 'All Buckets' },
    { value: 'future', label: 'Future Verses' },
    { value: 'daily', label: 'Daily Verses' },
    { value: 'weekly', label: 'Weekly Verses' },
    { value: 'monthly', label: 'Monthly Verses' },
    { value: 'quarterly', label: 'Quarterly Verses' }
  ];

  const fieldOptions = [
    { key: 'reference', label: 'Reference', required: true },
    { key: 'text', label: 'Verse Text', required: true },
    { key: 'version', label: 'Bible Version' },
    { key: 'status', label: 'Memorization Status' },
    { key: 'progress', label: 'Progress Percentage' },
    { key: 'tags', label: 'Tags' },
    { key: 'notes', label: 'Personal Notes' },
    { key: 'dateAdded', label: 'Date Added' },
    { key: 'lastStudied', label: 'Last Studied' }
  ];

  const getFilteredVerses = () => {
    let filtered = [...verses];

    if (exportFilters?.status !== 'all') {
      filtered = filtered?.filter(verse => verse?.status === exportFilters?.status);
    }

    if (exportFilters?.bucket !== 'all') {
      filtered = filtered?.filter(verse => verse?.bucket === exportFilters?.bucket);
    }

    if (exportFilters?.hasNotes) {
      filtered = filtered?.filter(verse => verse?.hasNotes);
    }

    if (exportFilters?.hasLinks) {
      filtered = filtered?.filter(verse => verse?.hasLinks);
    }

    return filtered;
  };

  const generateCSV = (versesToExport) => {
    const selectedFields = Object.keys(exportFields)?.filter(key => exportFields?.[key]);
    const headers = selectedFields?.map(field => {
      const fieldOption = fieldOptions?.find(f => f?.key === field);
      return fieldOption?.label || field;
    });

    const csvContent = [
      headers?.join(','),
      ...versesToExport?.map(verse => {
        return selectedFields?.map(field => {
          let value = verse?.[field] || '';
          
          // Special handling for different field types
          switch (field) {
            case 'tags':
              value = Array.isArray(verse?.tags) ? verse?.tags?.join('; ') : '';
              break;
            case 'progress':
              value = `${verse?.progress || 0}%`;
              break;
            case 'notes':
              // Escape quotes and newlines for CSV
              value = (verse?.notes || '')?.replace(/"/g, '""')?.replace(/\n/g, ' ');
              break;
            case 'text':
              // Escape quotes for CSV
              value = (verse?.text || '')?.replace(/"/g, '""');
              break;
            default:
              value = String(value);
          }
          
          // Wrap in quotes if contains comma, quote, or newline
          if (value?.includes(',') || value?.includes('"') || value?.includes('\n')) {
            return `"${value}"`;
          }
          return value;
        })?.join(',');
      })
    ]?.join('\n');

    return csvContent;
  };

  const generateTXT = (versesToExport) => {
    return versesToExport?.map(verse => {
      let line = `${verse?.reference}`;
      
      if (exportFields?.text && verse?.text) {
        line += ` - ${verse?.text}`;
      }
      
      if (exportFields?.version && verse?.version) {
        line += ` (${verse?.version})`;
      }
      
      if (exportFields?.notes && verse?.notes) {
        line += `\nNotes: ${verse?.notes}`;
      }
      
      if (exportFields?.tags && verse?.tags?.length > 0) {
        line += `\nTags: ${verse?.tags?.join(', ')}`;
      }
      
      return line;
    })?.join('\n\n');
  };

  const generateJSON = (versesToExport) => {
    const selectedFields = Object.keys(exportFields)?.filter(key => exportFields?.[key]);
    
    const exportData = versesToExport?.map(verse => {
      const exportVerse = {};
      selectedFields?.forEach(field => {
        exportVerse[field] = verse?.[field];
      });
      return exportVerse;
    });

    return JSON.stringify({
      exportedAt: new Date()?.toISOString(),
      totalVerses: exportData?.length,
      exportFields: selectedFields,
      verses: exportData
    }, null, 2);
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    const versesToExport = getFilteredVerses();
    const timestamp = new Date()?.toISOString()?.split('T')?.[0];
    
    let content, filename, mimeType;
    
    switch (exportFormat) {
      case 'csv':
        content = generateCSV(versesToExport);
        filename = `bible-verses-${timestamp}.csv`;
        mimeType = 'text/csv';
        break;
      case 'txt':
        content = generateTXT(versesToExport);
        filename = `bible-verses-${timestamp}.txt`;
        mimeType = 'text/plain';
        break;
      case 'json':
        content = generateJSON(versesToExport);
        filename = `bible-verses-${timestamp}.json`;
        mimeType = 'application/json';
        break;
      default:
        content = generateCSV(versesToExport);
        filename = `bible-verses-${timestamp}.csv`;
        mimeType = 'text/csv';
    }
    
    // Simulate export delay
    setTimeout(() => {
      downloadFile(content, filename, mimeType);
      setIsExporting(false);
      onClose();
    }, 1000);
  };

  const handleFieldToggle = (fieldKey) => {
    const field = fieldOptions?.find(f => f?.key === fieldKey);
    if (field?.required) return; // Don't allow toggling required fields
    
    setExportFields(prev => ({
      ...prev,
      [fieldKey]: !prev?.[fieldKey]
    }));
  };

  const selectAllFields = () => {
    const allFields = {};
    fieldOptions?.forEach(field => {
      allFields[field.key] = true;
    });
    setExportFields(allFields);
  };

  const selectMinimalFields = () => {
    const minimalFields = {};
    fieldOptions?.forEach(field => {
      minimalFields[field.key] = field?.required || false;
    });
    setExportFields(minimalFields);
  };

  if (!isOpen) return null;

  const filteredVersesCount = getFilteredVerses()?.length;
  const selectedFieldsCount = Object.values(exportFields)?.filter(Boolean)?.length;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card border border-border rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Export Verses
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Export your Bible verses to various file formats
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="p-6 space-y-6">
            {/* Export Format */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Export Format
              </label>
              <Select
                options={formatOptions}
                value={exportFormat}
                onChange={setExportFormat}
                placeholder="Select format..."
              />
            </div>

            {/* Export Filters */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-3">Filter Verses</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Status
                  </label>
                  <Select
                    options={statusOptions}
                    value={exportFilters?.status}
                    onChange={(value) => setExportFilters(prev => ({ ...prev, status: value }))}
                    placeholder="Filter by status..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Bucket
                  </label>
                  <Select
                    options={bucketOptions}
                    value={exportFilters?.bucket}
                    onChange={(value) => setExportFilters(prev => ({ ...prev, bucket: value }))}
                    placeholder="Filter by bucket..."
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-6 mt-4">
                <Checkbox
                  id="hasNotes"
                  checked={exportFilters?.hasNotes}
                  onChange={(checked) => setExportFilters(prev => ({ ...prev, hasNotes: checked }))}
                  label="Only verses with notes"
                />
                <Checkbox
                  id="hasLinks"
                  checked={exportFilters?.hasLinks}
                  onChange={(checked) => setExportFilters(prev => ({ ...prev, hasLinks: checked }))}
                  label="Only verses with links"
                />
              </div>
            </div>

            {/* Export Fields */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-foreground">Export Fields</h3>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectMinimalFields}
                  >
                    Minimal
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllFields}
                  >
                    Select All
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {fieldOptions?.map((field) => (
                  <div
                    key={field?.key}
                    className={`flex items-center space-x-3 p-3 rounded-lg border ${
                      field?.required 
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' :'bg-muted border-border'
                    }`}
                  >
                    <Checkbox
                      id={field?.key}
                      checked={exportFields?.[field?.key]}
                      onChange={() => handleFieldToggle(field?.key)}
                      disabled={field?.required}
                    />
                    <div className="flex-1">
                      <label 
                        htmlFor={field?.key}
                        className={`text-sm font-medium ${
                          field?.required ? 'text-blue-800 dark:text-blue-200' : 'text-foreground'
                        } cursor-pointer`}
                      >
                        {field?.label}
                        {field?.required && (
                          <span className="text-blue-600 dark:text-blue-400 ml-1">*</span>
                        )}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Export Summary */}
            <div className="bg-muted rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-2">Export Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Verses to export:</span>
                  <span className="font-medium text-foreground ml-2">{filteredVersesCount}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Fields to include:</span>
                  <span className="font-medium text-foreground ml-2">{selectedFieldsCount}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Format:</span>
                  <span className="font-medium text-foreground ml-2 uppercase">{exportFormat}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">File size (est.):</span>
                  <span className="font-medium text-foreground ml-2">
                    {Math.ceil(filteredVersesCount * selectedFieldsCount * 0.1)}KB
                  </span>
                </div>
              </div>
            </div>

            {/* Format Preview */}
            {exportFormat === 'csv' && (
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-2">CSV Preview</h4>
                <code className="text-xs text-muted-foreground block">
                  Reference,Verse Text,Bible Version{exportFields?.notes ? ',Personal Notes' : ''}
                  <br />
                  "John 3:16","For God so loved the world...","NIV"{exportFields?.notes ? ',"This verse reminds me..."' : ''}
                </code>
              </div>
            )}

            {exportFormat === 'txt' && (
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-2">Text Preview</h4>
                <code className="text-xs text-muted-foreground block whitespace-pre-line">
                  John 3:16 - For God so loved the world... (NIV)
                  {exportFields?.notes && 'Notes: This verse reminds me...'}
                  {exportFields?.tags && 'Tags: love, salvation'}
                </code>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-border">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleExport}
                disabled={filteredVersesCount === 0 || isExporting}
                iconName={isExporting ? "Loader2" : "Download"}
                iconPosition="left"
              >
                {isExporting ? 'Exporting...' : `Export ${filteredVersesCount} Verses`}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkExportModal;